/* eslint-disable react-refresh/only-export-components */
import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import type { RequiredWithOptionalUndefined } from '@eigtech/types'
import { cn } from '@eigtech/ui/lib/utils'

export type TooltipProps = React.ComponentProps<typeof TooltipPrimitive.Root> &
  React.ComponentProps<typeof TooltipPrimitive.Provider>

function Tooltip({ closeDelay, delay = 0, timeout, ...props }: TooltipProps) {
  type ProviderProps = Omit<React.ComponentProps<typeof TooltipPrimitive.Provider>, 'children'>
  const providerProps = {
    closeDelay,
    delay,
    timeout,
  } satisfies RequiredWithOptionalUndefined<ProviderProps>

  return (
    <TooltipPrimitive.Provider data-slot="tooltip-provider" {...providerProps}>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipPrimitive.Provider>
  )
}

export type TooltipTriggerProps = React.ComponentProps<typeof TooltipPrimitive.Trigger>

function TooltipTrigger({ ...props }: TooltipTriggerProps) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

const tooltipVariants = cva(
  'animate-in fade-in-0 zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-55 origin-(--radix-tooltip-content-transform-origin) w-fit text-balance rounded-md px-3 py-1.5 text-xs',
  {
    variants: {
      variant: {
        default:
          'dark:border-border bg-zinc-950 text-white shadow-md shadow-black/5 dark:border dark:bg-zinc-300 dark:text-black',
        light: 'border-border bg-background text-foreground border shadow-md shadow-black/5',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

export interface TooltipContentProps
  extends
    React.ComponentProps<typeof TooltipPrimitive.Popup>,
    VariantProps<typeof tooltipVariants> {
  sideOffset?: TooltipPrimitive.Positioner.Props['sideOffset']
  side?: TooltipPrimitive.Positioner.Props['side']
  align?: TooltipPrimitive.Positioner.Props['align']
  alignOffset?: TooltipPrimitive.Positioner.Props['alignOffset']
  showArrow?: boolean
}

function TooltipContent({
  className,
  children,
  variant,
  sideOffset = 8,
  showArrow = true,
  align,
  alignOffset = 0,
  side,
  ...props
}: TooltipContentProps) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        className="z-50"
        data-slot="tooltip-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <TooltipPrimitive.Popup
          className={cn(tooltipVariants({ variant }), className)}
          data-slot="tooltip-content"
          {...props}
        >
          {children}
          {showArrow && <TooltipArrow variant={variant} />}
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

const tooltipArrowBackgroundVariants = cva('', {
  variants: {
    variant: {
      default: 'fill-zinc-950 dark:fill-zinc-300',
      light: 'fill-background',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const tooltipArrowStrokeVariants = cva('', {
  variants: {
    variant: {
      default: '',
      light: 'stroke-background',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export interface TooltipArrowProps
  extends
    React.ComponentProps<typeof TooltipPrimitive.Arrow>,
    VariantProps<typeof tooltipArrowBackgroundVariants> {}

function TooltipArrow({ className, variant, ...props }: TooltipArrowProps) {
  return (
    <TooltipPrimitive.Arrow
      className={cn(
        'z-50 data-[side=bottom]:top-[-8px] data-[side=left]:right-[-13px] data-[side=right]:left-[-13px] data-[side=top]:bottom-[-8px] data-[side=left]:rotate-90 data-[side=right]:-rotate-90 data-[side=top]:rotate-180',
        className
      )}
      data-slot="tooltip-arrow"
      {...props}
    >
      <svg fill="none" height="10" viewBox="0 0 20 10" width="20">
        <path
          className={cn(tooltipArrowBackgroundVariants({ variant }))}
          d="M9.66437 2.60207L4.80758 6.97318C4.07308 7.63423 3.11989 8 2.13172 8H0V9H20V8H18.5349C17.5468 8 16.5936 7.63423 15.8591 6.97318L11.0023 2.60207C10.622 2.2598 10.0447 2.25979 9.66437 2.60207Z"
        />
        <path
          className={cn(tooltipArrowStrokeVariants({ variant }))}
          d="M10.3333 3.34539L5.47654 7.71648C4.55842 8.54279 3.36693 9 2.13172 9H0V8H2.13172C3.11989 8 4.07308 7.63423 4.80758 6.97318L9.66437 2.60207C10.0447 2.25979 10.622 2.2598 11.0023 2.60207L15.8591 6.97318C16.5936 7.63423 17.5468 8 18.5349 8H20V9H18.5349C17.2998 9 16.1083 8.54278 15.1901 7.71648L10.3333 3.34539Z"
        />
      </svg>
    </TooltipPrimitive.Arrow>
  )
}

export { Tooltip, TooltipArrow, TooltipContent, TooltipTrigger, tooltipVariants }
